import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Car, Menu, X, Search, ChevronLeft } from 'lucide-react';
import { getTradeBySlug } from '../../config/trades';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Home,
  Car,
};

interface SearchFormProps {
  postcode: string;
  service: string;
  searching: boolean;
  onPostcodeChange: (value: string) => void;
  onServiceChange: (value: string) => void;
  onSubmit: (e: React.FormEvent, serviceType?: string) => void;
  trade?: string;
}

const BUTTON_COLORS: { bg: string; border: string; hover: string; ring: string }[] = [
  { bg: 'bg-blue-600', border: 'border-blue-500', hover: 'hover:bg-blue-700', ring: 'focus:ring-blue-500' },
  { bg: 'bg-green-600', border: 'border-green-500', hover: 'hover:bg-green-700', ring: 'focus:ring-green-500' },
  { bg: 'bg-amber-600', border: 'border-amber-500', hover: 'hover:bg-amber-700', ring: 'focus:ring-amber-500' },
  { bg: 'bg-violet-600', border: 'border-violet-500', hover: 'hover:bg-violet-700', ring: 'focus:ring-violet-500' },
];

export default function SearchForm({
  postcode,
  service,
  searching,
  onPostcodeChange,
  onServiceChange,
  onSubmit,
  trade = 'locksmith',
}: SearchFormProps) {
  const navigate = useNavigate();
  // Default to collapsed state on mobile since postcode is pre-filled from landing page
  const [isExpanded, setIsExpanded] = useState(false);

  const tradeConfig = getTradeBySlug(trade);
  const subServices = tradeConfig?.subServices ?? [];

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="fixed top-4 left-4 z-20 p-2 bg-white rounded-full shadow-md lg:hidden"
        aria-label={isExpanded ? 'Collapse search' : 'Expand search'}
      >
        {isExpanded ? (
          <X className="w-5 h-5" />
        ) : (
          <Menu className="w-5 h-5" />
        )}
      </button>

      {/* Search Panel */}
      <div
        className={`fixed top-0 left-0 z-10 h-full transition-transform duration-300 lg:transition-none lg:transform-none lg:top-4 lg:left-4 lg:h-auto ${
          isExpanded ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="bg-white rounded-lg shadow-xl w-80 h-full lg:h-auto">
          <div className="p-5">
            {/* Close button for mobile */}
            <div className="flex justify-end lg:hidden -mt-2 -mr-2 mb-4">
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 text-gray-500 hover:text-gray-700 rounded-full"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Enter your Postcode
                </label>
                <input
                  type="text"
                  placeholder="e.g., SW1A 1AA"
                  value={postcode}
                  onChange={(e) => onPostcodeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Sub-service buttons — only shown when the trade has sub-services */}
              {subServices.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Search For
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    {subServices.map((sub, index) => {
                      const colors = BUTTON_COLORS[index % BUTTON_COLORS.length];
                      const IconComponent = ICON_MAP[sub.icon];
                      return (
                        <button
                          key={sub.slug}
                          type="button"
                          className={`flex-1 flex items-center justify-center px-4 py-2 border-2 ${colors.border} text-sm font-medium rounded-md shadow-lg text-white ${colors.bg} ${colors.hover} focus:outline-none focus:ring-2 focus:ring-offset-2 ${colors.ring} ${
                            searching ? 'opacity-75' : ''
                          }`}
                          disabled={searching}
                          onClick={(e) => {
                            onServiceChange(sub.slug);
                            onSubmit(e, sub.slug);
                          }}
                        >
                          {searching && service === sub.slug ? (
                            'Searching...'
                          ) : (
                            <>
                              {IconComponent && <IconComponent className="mr-2 h-5 w-5 flex-shrink-0" />}
                              <span>View {sub.label} {tradeConfig?.pluralLabel ?? 'Services'}</span>
                            </>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Search button — always visible, especially useful on mobile and for trades without sub-services */}
              <button
                type="button"
                className={`w-full flex items-center justify-center px-4 py-2 border-2 border-primary text-sm font-medium rounded-md shadow-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                  searching ? 'opacity-75' : ''
                }`}
                disabled={searching}
                onClick={(e) => onSubmit(e)}
              >
                {searching ? (
                  'Searching...'
                ) : (
                  <>
                    <Search className="mr-2 h-5 w-5 flex-shrink-0" />
                    <span>Search</span>
                  </>
                )}
              </button>

              {/* Home button */}
              <button
                type="button"
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                onClick={() => navigate('/')}
              >
                <ChevronLeft className="mr-1 h-4 w-4 flex-shrink-0" />
                <span>Back to Home</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
